import React, { useState, useContext, useMemo, useEffect } from "react";
import "./styles.css";
import { Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { CloseIcon, VehicleBatteryVolt, VideoIcon, IgnOffToOn, IgnOnToOff, VehicleFault, VehicleOk, VehicleOkOff, VehicleFaultOn, VehicleOffLocked, VehicleOnUnlocked, VehicleLowNetwork, VehicleMoving, Alert } from "../../../Assets/images/svgs";
import { PING_TEST_STEPS, IGNITION_STATE } from "../../../Library/constants";
import { AuthContext } from "../../../Library/contexts";
import LottieView from "../../../Components/Atoms/LottieView";
import { loader } from "../../../Assets/images/jsons"
import PATHS from "../../../Routes/paths";
import { useNavigate } from "react-router-dom";
import { formatTimestamp } from "../../../Library/datetime";
import { RelayInstallation } from "../../../Assets/images/gifs";
const IGN_TYPES = { 0: "OFF", 1: "ON" };
const VEHICLE_STATUS = { 1: "Locked", 0: "Unlocked" };

const CircularProgress = ({ circularProgress }) => {
  const [progress, setProgress] = useState(0);

  const radius = 50;
  const strokeWidth = 10;
  const circumference = 2 * Math.PI * radius;
  const progressOffset = circumference - (progress / 100) * circumference;

  useEffect(() => {
    if (circularProgress > progress) {
      setProgress(circularProgress);
    }
  }, [ circularProgress, progress ]);

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prgrs) => {
        if (prgrs >= 99) {
          clearInterval(progressInterval);
          return prgrs;
        }
        return prgrs + 1;
      });
    }, 1500);

    return () => clearInterval(progressInterval);
  }, []);

  return (
    <div className="progress-container">
      <svg
        className="progress-ring"
        width={radius * 2 + strokeWidth}
        height={radius * 2 + strokeWidth}
      >
        <circle
          className="progress-ring__background"
          stroke="#e6e6e6"
          strokeWidth={strokeWidth}
          fill="transparent"
          r={radius}
          cx={radius + strokeWidth / 2}
          cy={radius + strokeWidth / 2}
        />
        <circle
          className="progress-ring__circle"
          stroke="#007bff"
          strokeWidth={strokeWidth}
          fill="transparent"
          r={radius}
          cx={radius + strokeWidth / 2}
          cy={radius + strokeWidth / 2}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: progressOffset,
            transition: "stroke-dashoffset 0.35s",
          }}
        />
      </svg>
      <div className="progress-text">{`${progress}%`}</div>
    </div>
  );
};

const PingTest = ({ show, size = "md", handleModalClose, currentStep = null, ign, showInfo = false, handleIgnSwitch, progressMsg1, progressMsg2, handleRatedVoltContinue, devAndVehicleDetails, handleIgnVerified, still=false, handleIgnitionTestSuccess, handleStartRelayTest, relayTestStarted = false, relayTestCompleted = false, handleRelayTest, vehStat, handleRestartRetry, mismatch, hideClose, lowNetwork, lowVoltage, motionDetected, handlePerformSignalTest, circular, circularProgress, allowRetry, enableRelayRetry, retryCount }) => {
  const navigate = useNavigate();
  const { profile } = useContext(AuthContext);
  const [checked, setChecked] = useState(false);
  const [voltInput, setVoltInput] = useState(profile.inpExtVolt || "");

  const ignTestInfo = useMemo(() => {
    return [
      { label: "Rated Voltage", value: voltInput ? `${voltInput} V` : "-" },
      { label: "Detected Voltage", value: devAndVehicleDetails?.installationDetails?.extVolt ? `${devAndVehicleDetails?.installationDetails?.extVolt} V` : "-", cls: voltInput >= devAndVehicleDetails?.installationDetails?.extVolt ? "err" : ""},
      { label: "Ignition", value: (devAndVehicleDetails?.installationDetails?.ignition) ? IGN_TYPES[1] : IGN_TYPES[0] },
      { label: "Updated", value: devAndVehicleDetails?.installationDetails?.ts ? formatTimestamp(devAndVehicleDetails?.installationDetails?.ts * 1000) : "-" }
    ]
  },[devAndVehicleDetails?.installationDetails, voltInput]);

  const relayTestInfo = useMemo(() => {
    return [
      { label: "Vehicle Status", value: (vehStat === 0 || vehStat === 1) ? VEHICLE_STATUS[vehStat] : "-", cls: vehStat === 0 ? "ok" : "err" },
      { label: "Ignition", value: (devAndVehicleDetails?.installationDetails?.ignition) ? IGN_TYPES[1] : IGN_TYPES[0] },
      { label: "Updated", value: devAndVehicleDetails?.installationDetails?.ts ? formatTimestamp(devAndVehicleDetails?.installationDetails?.ts * 1000) : "-" },
      { label: "Retries Left", value: `${(3 - (retryCount || 0))}/3` },
    ]
  },[devAndVehicleDetails?.installationDetails, vehStat, retryCount]);

  const relayTestMeta = useMemo(() => {
    return [
      { label: "IntV", value: devAndVehicleDetails?.installationDetails?.intVolt ? `${devAndVehicleDetails?.installationDetails?.intVolt} V` : "-" },
      { label: "ExtV", value: devAndVehicleDetails?.installationDetails?.extVolt ? `${devAndVehicleDetails?.installationDetails?.extVolt} V` : "-"},
      { label: "Speed", value: (devAndVehicleDetails?.installationDetails?.spd === undefined || devAndVehicleDetails?.installationDetails?.spd == null) ? "-" : devAndVehicleDetails?.installationDetails?.spd },
      { label: "Ignition", value: (devAndVehicleDetails?.installationDetails?.ignition) ? IGN_TYPES[1] : IGN_TYPES[0] },
      { label: "Updated", value: devAndVehicleDetails?.installationDetails?.ts ? formatTimestamp(devAndVehicleDetails?.installationDetails?.ts * 1000, true) : "-" },
      { label: "Received", value: devAndVehicleDetails?.installationDetails?.receivedTs ? formatTimestamp(devAndVehicleDetails?.installationDetails?.receivedTs, true) : "-" }
    ]
  },[devAndVehicleDetails?.installationDetails?.extVolt, devAndVehicleDetails?.installationDetails?.ignition, devAndVehicleDetails?.installationDetails?.intVolt, devAndVehicleDetails?.installationDetails?.receivedTs, devAndVehicleDetails?.installationDetails?.spd, devAndVehicleDetails?.installationDetails?.ts]);

  const getInfoBar = () => {
    if (showInfo) {
      let itr = relayTestStarted ? circular ? relayTestMeta : relayTestInfo : ignTestInfo;
      return (
        <div className="info-cnt">
          {itr.map((kv,i) => (<span key={i}>{kv.label}<br/><span className={kv.cls ? kv.cls : ""}>{kv.value}</span></span>))}
        </div>
      );
    }
    return null;
  };

  const IgnitionSwitch = () => {
    return (
      <>
        <div className="start-test">
          {ign ? <IgnOnToOff className="vehicle-icon" /> : <IgnOffToOn className="vehicle-icon" />}
          <div className="start-test-inf">
            <span className="inf1">Turn {IGN_TYPES[ign ? IGNITION_STATE.OFF : IGNITION_STATE.ON]} the ignition key</span>
            <span className="inf2">
              { ign === IGNITION_STATE.OFF ? "Make sure you are in good network area." : "Turn OFF ignition key and confirm by checking the box below"}
            </span>
          </div>
        </div>
        <div className={`start-test-input-cnt ${showInfo ? "upd-mb" : ""}`}>
          <div>
            <input id="checkbox" type="checkbox" checked={checked} onChange={() => setChecked((prev) => !prev)} />
            <label htmlFor="checkbox">Yes, vehicle ignition is {IGN_TYPES[ign ? IGNITION_STATE.OFF : IGNITION_STATE.ON]}</label>
          </div>
          <button className={`start-test-btn ${!checked ? "disabled" : ""}`} onClick={relayTestStarted ? handleStartRelayTest : handleIgnSwitch} disabled={!checked}>
            Continue
          </button>
        </div>
      </>
    );
  }

  const InProgress = () => {
    return (
      <>
        {circular ? <CircularProgress circularProgress={circularProgress} /> : <LottieView animationData={loader} className="lottie-view"/> }
        <motion.p
          className="ign-mob-header"
          key={progressMsg1}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.8 }}
        >
          {progressMsg1}
        </motion.p>
        <motion.p
          className="ign-mob-sub-header"
          key={progressMsg2}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.8 }}
        >
          {progressMsg2}
        </motion.p>
      </>
    );
  };

  const RatedVoltageInput = () => {
    return (
      <>
        <div className="start-test">
          <VehicleBatteryVolt className="vehicle-icon" />
          <div className="start-test-inf">
            <span className="inf1">
              {profile.inpExtVolt ? "Confirm Rated Battery Voltage" : "Enter Rated Battery Voltage" }
            </span>
            <span className="inf2">
              {profile.inpExtVolt ? "Please confirm the voltage supplied by the vehicle's battery to the circuit." : "Please enter the voltage supplied by the vehicle's battery to the circuit."}
            </span>
          </div>
        </div>
        <div className={`start-test-input-cnt ${showInfo ? "upd-mb" : ""}`}>
          <div>
            <input
              type="text"
              value={voltInput}
              onChange={(e) => {
                const regex = /^[0-9.]*$/;
                if (regex.test(e.target.value)) setVoltInput(e.target.value);
              }}
              placeholder="Enter Battery Voltage"
              autoFocus={true}
            />
          </div>
          <button
            className={`start-test-btn ${!voltInput ? "disabled" : ""}`}
            onClick={handleRatedVoltContinue}
            disabled={!voltInput}
          >
            {profile.inpExtVolt ? "continue" : "Save"}
          </button>
        </div>
    </>
    )
  }

  const UnableToDetectIgn = () => {
    return (
      <>
        <div className="start-test">
          {lowNetwork ? <VehicleLowNetwork className="vehicle-icon"/> : still ? (allowRetry ? <img src={RelayInstallation} alt="" className="vehicle-icon unable-to-lock-unlock-image"/> : <VehicleFaultOn className="vehicle-icon"/>) : <VehicleFault className="vehicle-icon"/> }
          <div className={`start-test-inf ${allowRetry ? "unable-to-lock-unlock" : ""}`}>
            <span className="inf1"> {lowNetwork ? "Low Network Detected" : relayTestStarted ? mismatch ? "Wrong Ignition State" : vehStat === 0 ? "Unable to Lock Vehicle": "Unable to Unlock Vehicle" : (still ? "Ignition is Still ON" : "Unable to Detect Ignition ON") } </span>
            {lowNetwork?
               <>
               <p>1. Move vehicle in good network area.</p>
               <p>2. Avoid performing the test in parking basements and enclosed area.</p>
               <button className="start-test-btn" onClick={handlePerformSignalTest}>Retry</button>
             </>
            : still ? <>
              <p>{relayTestStarted ? (allowRetry ? "Ensure installation is correct & orange wire is connected to output wire" : "Check for the correct installation and make sure ignition wire is connected to switching out wire of relay.") : "Check for the correct installation and perform the test again"}</p>
              {!allowRetry && <span className="vid-link" onClick={() => navigate(PATHS.INSTALLATION_VIDEO)  }>(<VideoIcon/> Watch The Installation Video )</span>}
              <button disabled={allowRetry && !enableRelayRetry} className={`start-test-btn ${allowRetry ? "retry-btn" : ""} ${allowRetry && !enableRelayRetry ? "disable-btn" : ""}`} onClick={() => handleRestartRetry((relayTestStarted? mismatch ? "Testing was reset due to wrong ignition state" :  vehStat === 0 ? "Testing was reset due to failure in vehicle Locking. Check for the proper installation and start again" : "Testing was reset due to failure in vehicle Unlocking. Check for the proper installation and start again" : "Testing was reset because vehicle Ignition OFF was not detected. Check for the proper installation and start again"), true, !!allowRetry)}>{allowRetry ? "Restart Immobilisation" : "Restart"}</button>
              {allowRetry && !enableRelayRetry ? <p className="enabling-retry-msg">Enabling retry, please wait...</p> : ""}
            </> : <>
              <p>1. Ignition should be ON</p>
              <p>2. Vehicle should be in good network</p>
              <p>3. Installation must be correct</p>
              <span className="vid-link" onClick={() => navigate(PATHS.INSTALLATION_VIDEO)}>(<VideoIcon/> Watch The Installation Video )</span>
            </>}
          </div>
        </div>
        {still || lowNetwork ? null : <div className={`start-test-input-cnt ${showInfo ? "upd-mb" : ""}`}>
          <div>
            <input id="checkbox" type="checkbox" checked={checked} onChange={() => setChecked((prev) => !prev)} />
            <label htmlFor="checkbox">Yes, vehicle ignition is {IGN_TYPES[ign === 0 ? 1 : 0]} now</label>
          </div>
          <button
            className={`start-test-btn ${!checked ? "disabled" : ""}`}
            onClick={relayTestStarted ? handleStartRelayTest : handleRatedVoltContinue}
            disabled={!checked}
          >
            Retry
          </button>
        </div>}
      </>
    );
  };

  const IgnDetected = () => {
    const isbatteryLow = (voltInput >= devAndVehicleDetails?.installationDetails?.extVolt) || lowVoltage;
    return (
      <div className="start-test">
        {motionDetected ? <VehicleMoving className="vehicle-icon"/> : isbatteryLow ? <VehicleFault className="vehicle-icon"/> : ign === 1 ? <VehicleOk className="vehicle-icon"/> : <VehicleOkOff className="vehicle-icon"/>}
        <div className="start-test-inf">
          <span className="inf1">{motionDetected && <Alert />} {motionDetected ? "Vehicle is moving!" : isbatteryLow ? "Low Battery Voltage" : `Ignition ${IGN_TYPES[ign]} Verified`} </span>
          <span className="inf2"> {motionDetected ? "Testing interrupted due to vehicle movement. Please ensure the vehicle is parked and restart the test." : isbatteryLow ? "Vehicle battery is not sufficiently charged. Please charge it and restart the ignition testing again." : `Vehicle’s ignition is now ${IGN_TYPES[ign]}. Press “Continue” to move forward.`} </span>
          <button className="start-test-btn" onClick={() => { isbatteryLow || motionDetected ? handleRestartRetry(motionDetected ? "Testing was reset due to vehicle movement." : "Testing was reset because Low battery voltage was detected. Charge your vehicle battery and start again", (motionDetected || isbatteryLow) ? true : false) : ign === 0 ? handleIgnitionTestSuccess(voltInput) : handleIgnVerified() }}>
            {motionDetected ? "Restart" : isbatteryLow ? "Restart" : "continue"}
          </button>
        </div>
      </div>
    );
  }

  const RealyTest = () => {
    return (
      <div className="start-test">
        {ign === IGNITION_STATE.ON || relayTestCompleted ? <VehicleOnUnlocked className="vehicle-icon"/> : <VehicleOffLocked className="vehicle-icon"/>}
        <div className="start-test-inf">
          <span className="inf1">{ign === IGNITION_STATE.ON ? (relayTestCompleted ? "Vehicle Unlocked" : "Lock Your Vehicle") : "Vehicle Locked"}</span>
          <span className="inf2">{ign === IGNITION_STATE.ON ? (relayTestCompleted ? "Vehicle is currently Unlocked. Click on “continue” to move forward" : "Vehicle is currently Unlocked. Click on “Lock Vehicle” to move forward.") : "Vehicle is currently Locked. Click on “Unlock vehicle” to unlock your vehicle and move forward"}</span>
          <button className="start-test-btn" onClick={handleRelayTest}>
            {ign === IGNITION_STATE.ON ? (relayTestCompleted ? "Continue" : "Lock Vehicle") : "Unlock vehicle"}
          </button>
        </div>
      </div>
    );
  }

  const getContent = () => {
    switch (currentStep) {
      case PING_TEST_STEPS.IGN_SWITCH: return IgnitionSwitch();
      case PING_TEST_STEPS.IN_PROGRESS: return InProgress();
      case PING_TEST_STEPS.RATED_VOLT_INP: return RatedVoltageInput();
      case PING_TEST_STEPS.UNABLE_DETECT_IGN: return UnableToDetectIgn();
      case PING_TEST_STEPS.IGN_DETECTED: return IgnDetected();
      case PING_TEST_STEPS.RELAY_TEST: return RealyTest();
      default: return null;
    }
  };

  return (
    <Modal isOpen={show} isCentered size={size}>
      <ModalOverlay />
      <ModalContent className="ign-mob-content">
        <ModalBody className="ign-mob-body">
          {getContent()}
          {getInfoBar()}
          {!hideClose && <CloseIcon className="ign-mob-close-btn" onClick={() => handleModalClose()} />}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PingTest;